import { useRef } from "react";
import { Mic, Square } from "lucide-react";
import { useSpeechRecognition } from "@/v2/hooks/useSpeechRecognition";

/**
 * Small mic button for free-text fields. Appends the dictated text to the
 * existing value, updating it live while the user speaks.
 * Renders nothing if the browser has no Web Speech API support.
 */
export default function DictationButton({
  value,
  onChange,
  label = "Detta il testo con la voce",
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  className?: string;
}) {
  const baseRef = useRef("");

  const { supported, isListening, error, toggle } = useSpeechRecognition(
    (text) => {
      const base = baseRef.current;
      onChange(base ? `${base} ${text}` : text);
    },
  );

  if (!supported) return null;

  return (
    <span className={`inline-flex flex-col items-end gap-1 ${className}`}>
      <button
        type="button"
        onClick={() => {
          if (!isListening) baseRef.current = value.trim();
          toggle();
        }}
        aria-label={isListening ? "Interrompi dettatura" : label}
        title={isListening ? "Interrompi dettatura" : label}
        aria-pressed={isListening}
        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors ${
          isListening
            ? "border-destructive/60 bg-destructive/20 text-destructive-foreground animate-pulse"
            : "border-white/10 bg-white/5 text-slate-300 hover:text-white hover:border-white/25"
        }`}
      >
        {isListening ? <Square size={13} /> : <Mic size={14} />}
      </button>
      {error && (
        <span role="alert" className="text-[11px] text-red-300 max-w-[220px] text-right">
          {error}
        </span>
      )}
    </span>
  );
}
